import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTABanner from "../components/CTABanner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Calendar, Clock, Search, User } from "lucide-react";
import Seo, { buildBreadcrumb } from "../components/Seo";
import { PAGE_SEO } from "../data/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "All",
  "Tower Hoists",
  "Passenger Hoists",
  "Monkey Lifts",
  "Rebar Machinery",
  "Concrete Equipment",
  "Construction Safety",
  "Buying Guides",
];

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => setPosts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rest.filter((p) => {
      const catMatch = activeCat === "All" || p.category === activeCat;
      const qMatch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q);
      return catMatch && qMatch;
    });
  }, [rest, activeCat, query]);

  const seo = PAGE_SEO.insights;

  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        image={seo.image}
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
          ]),
        ]}
        jsonLdId="insights"
      />
      <Navbar />

      {/* Page Header */}
      <section
        data-testid="insights-hero"
        className="pt-32 pb-16 md:pt-40 md:pb-20 bg-liftile-black relative overflow-hidden"
      >
        <div className="absolute inset-0 industrial-grid opacity-20" />
        <div
          aria-hidden
          className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.18]"
          style={{
            background:
              "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
            filter: "blur(60px)",
          }}
        />

        <div className="container-x relative max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="heading-eyebrow mb-4"
          >
            Knowledge Center
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="insights-heading"
            className="display-1 text-liftile-cream"
          >
            Construction <span className="text-liftile-orange">Insights</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="font-body text-liftile-cream/70 mt-6 text-base md:text-lg leading-relaxed max-w-3xl"
          >
            Expert insights, buying guides, engineering knowledge and
            construction equipment resources from the LIFTILE Engineering Team.
          </motion.p>
        </div>
      </section>

      {/* Featured Article */}
      {!loading && featured && (
        <section
          data-testid="insights-featured-section"
          className="bg-liftile-cream-light pt-16 md:pt-20"
        >
          <div className="container-x">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7 }}
              className="mb-6 flex items-center gap-3"
            >
              <span className="w-10 h-px bg-liftile-orange" />
              <span className="font-heading text-[10px] uppercase tracking-[0.3em] text-liftile-orange font-semibold">
                Featured Article
              </span>
            </motion.div>

            <motion.article
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
              data-testid="insights-featured"
              className="group grid lg:grid-cols-[1.15fr_1fr] gap-0 bg-white rounded-2xl overflow-hidden shadow-[0_10px_40px_-12px_rgba(0,0,0,0.10)] border border-liftile-matte-black/[0.04] hover:shadow-[0_22px_60px_-12px_rgba(0,0,0,0.15)] transition-shadow duration-500"
            >
              <Link
                to={`/insights/${featured.slug}`}
                data-testid={`insights-featured-link-${featured.slug}`}
                className="block aspect-[16/10] lg:aspect-auto overflow-hidden"
              >
                <img
                  src={featured.cover_image}
                  alt={featured.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                />
              </Link>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-liftile-matte-black/55 mb-5">
                  <span className="text-liftile-orange font-heading uppercase tracking-[0.2em] font-semibold">
                    {featured.category}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {featured.read_time}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />{" "}
                    {formatDate(featured.published_at)}
                  </span>
                </div>
                <Link to={`/insights/${featured.slug}`}>
                  <h2 className="font-display uppercase text-liftile-matte-black tracking-tight text-2xl sm:text-3xl md:text-4xl leading-[1.1] hover:text-liftile-orange transition-colors duration-300">
                    {featured.title}
                  </h2>
                </Link>
                <div className="flex items-center gap-2 mt-5 text-xs text-liftile-matte-black/55 font-body">
                  <User className="w-3.5 h-3.5 text-liftile-orange" />
                  <span>{featured.author}</span>
                </div>
                <p className="font-body text-base md:text-[17px] text-liftile-matte-black/70 mt-6 leading-relaxed">
                  {featured.excerpt}
                </p>
                <Link
                  to={`/insights/${featured.slug}`}
                  data-testid="insights-featured-cta"
                  aria-label={`Read the full article: ${featured.title}`}
                  className="mt-8 inline-flex items-center gap-3 self-start bg-liftile-matte-black text-white font-heading text-xs uppercase tracking-[0.2em] font-semibold px-7 py-4 rounded-xl hover:bg-liftile-orange transition-colors duration-300"
                >
                  Read Article <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.article>
          </div>
        </section>
      )}

      {/* Filters + Search + Grid */}
      <section
        data-testid="insights-grid-section"
        className="bg-liftile-cream-light py-16 md:py-24 text-liftile-matte-black"
      >
        <div className="container-x">
          {/* Filters + Search Bar */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8 mb-12">
            <div
              data-testid="insights-search"
              className="relative w-full lg:w-[340px] shrink-0"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-liftile-matte-black/40" />
              <input
                type="text"
                placeholder="Search Construction Insights..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="insights-search-input"
                className="w-full bg-white border border-liftile-matte-black/10 rounded-full pl-11 pr-5 py-3.5 font-body text-sm text-liftile-matte-black placeholder:text-liftile-matte-black/40 focus:outline-none focus:border-liftile-orange focus:ring-4 focus:ring-liftile-orange/10 transition-all duration-300"
              />
            </div>

            <div
              data-testid="insights-filters"
              className="flex flex-wrap items-center gap-2 flex-1"
            >
              {CATEGORIES.map((cat) => {
                const active = cat === activeCat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCat(cat)}
                    data-testid={`insights-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`relative font-heading text-[11px] uppercase tracking-[0.18em] font-semibold px-4 py-2.5 rounded-full transition-all duration-300 ${
                      active
                        ? "bg-liftile-matte-black text-white"
                        : "bg-white text-liftile-matte-black/70 border border-liftile-matte-black/10 hover:border-liftile-orange hover:text-liftile-orange"
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div
              data-testid="insights-loading"
              className="text-center py-20 font-heading uppercase tracking-wider text-liftile-matte-black/40"
            >
              Loading articles...
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeCat}-${query}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
              >
                {filtered.length === 0 ? (
                  <div
                    data-testid="insights-empty"
                    className="text-center py-16 font-body text-liftile-matte-black/55"
                  >
                    No articles match your search. Try a different category or
                    keyword.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 md:gap-8">
                    {filtered.map((post, i) => (
                      <ArticleCard key={post.id} post={post} index={i} />
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </section>

      <CTABanner
        eyebrow="Stay Curious"
        headline="Need Help Choosing the Right Construction Equipment?"
        description="Our engineering team is ready to help you select the right LIFTILE solution for your project — from passenger hoists and tower hoists to rebar machinery and beyond."
        primaryLabel="Request a Quote"
        primaryHref="/contact"
        secondaryLabel="Explore Products"
        secondaryHref="/products"
      />

      <Footer />
    </>
  );
}

/* ---------- Premium Article Card ---------- */
export function ArticleCard({ post, index = 0 }) {
  return (
    <motion.article
      data-testid={`insights-card-${post.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        duration: 0.55,
        delay: index * 0.06,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group h-full flex flex-col bg-white rounded-2xl overflow-hidden shadow-[0_4px_18px_rgba(0,0,0,0.05)] hover:shadow-[0_18px_45px_-12px_rgba(0,0,0,0.18)] border border-transparent hover:border-liftile-orange/30 hover:-translate-y-1.5 transition-all duration-500"
    >
      <Link
        to={`/insights/${post.slug}`}
        className="block aspect-[16/10] overflow-hidden"
      >
        <img
          src={post.cover_image}
          alt={post.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
        />
      </Link>
      <div className="p-6 md:p-7 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-liftile-matte-black/55 mb-3">
          <span className="text-liftile-orange font-heading uppercase tracking-[0.18em] font-semibold">
            {post.category}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> {post.read_time}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {formatDate(post.published_at)}
          </span>
        </div>
        <Link to={`/insights/${post.slug}`}>
          <h3 className="font-heading text-lg leading-snug font-semibold text-liftile-matte-black group-hover:text-liftile-orange transition-colors duration-300 line-clamp-3">
            {post.title}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-3 text-[11px] text-liftile-matte-black/55 font-body">
          <User className="w-3 h-3 text-liftile-orange" />
          <span>{post.author}</span>
        </div>
        <p className="font-body text-sm text-liftile-matte-black/70 mt-4 leading-relaxed line-clamp-3 flex-1">
          {post.excerpt}
        </p>
        <Link
          to={`/insights/${post.slug}`}
          data-testid={`insights-card-cta-${post.slug}`}
          aria-label={`Read the full article: ${post.title}`}
          className="mt-6 inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.2em] font-semibold text-liftile-orange self-start hover:gap-3 transition-all duration-300"
        >
          Read Article <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </motion.article>
  );
}
